import React, { forwardRef } from "react"

import * as Headless from "@headlessui/react"
import clsx from "clsx"

import { CONTROL_HEIGHT_CLASS } from "./control"

export function InputGroup({
  children,
}: React.ComponentPropsWithoutRef<`span`>) {
  return (
    <span
      data-slot="control"
      className={clsx(
        `relative isolate block`,
        `has-[[data-slot=icon]:first-child]:[&_input]:pl-10 has-[[data-slot=icon]:last-child]:[&_input]:pr-10 sm:has-[[data-slot=icon]:first-child]:[&_input]:pl-8 sm:has-[[data-slot=icon]:last-child]:[&_input]:pr-8`,
        `*:data-[slot=icon]:pointer-events-none *:data-[slot=icon]:absolute *:data-[slot=icon]:top-1/2 *:data-[slot=icon]:z-10 *:data-[slot=icon]:size-5 *:data-[slot=icon]:-translate-y-1/2 sm:*:data-[slot=icon]:size-4`,
        `[&>[data-slot=icon]:first-child]:left-3 sm:[&>[data-slot=icon]:first-child]:left-2.5 [&>[data-slot=icon]:last-child]:right-3 sm:[&>[data-slot=icon]:last-child]:right-2.5`,
        `*:data-[slot=icon]:text-muted`,
      )}
    >
      {children}
    </span>
  )
}

const dateTypes = [`date`, `datetime-local`, `month`, `time`, `week`]
type DateType = (typeof dateTypes)[number]

export type InputProps = Omit<Headless.InputProps, `as` | `className`> & {
  className?: string
  type?:
    | DateType
    | `email`
    | `number`
    | `password`
    | `search`
    | `tel`
    | `text`
    | `url`
}

export const Input = forwardRef(function Input(
  { className, ...props }: InputProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  return (
    <span
      data-slot="control"
      className={clsx([
        className,
        `relative block w-full`,
        `before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-background before:shadow-sm`,
        `after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset sm:focus-within:after:ring-2 sm:focus-within:after:ring-focus-ring`,
        `has-data-disabled:opacity-50 has-data-disabled:before:bg-surface has-data-disabled:before:shadow-none`,
      ])}
    >
      <Headless.Input
        ref={ref}
        {...props}
        className={clsx([
          props.type &&
            dateTypes.includes(props.type) && [
              `[&::-webkit-datetime-edit-fields-wrapper]:p-0`,
              `[&::-webkit-date-and-time-value]:min-h-[1.5em]`,
              `[&::-webkit-datetime-edit]:inline-flex`,
              `[&::-webkit-datetime-edit]:p-0`,
              `[&::-webkit-datetime-edit-year-field]:p-0`,
              `[&::-webkit-datetime-edit-month-field]:p-0`,
              `[&::-webkit-datetime-edit-day-field]:p-0`,
              `[&::-webkit-datetime-edit-hour-field]:p-0`,
              `[&::-webkit-datetime-edit-minute-field]:p-0`,
              `[&::-webkit-datetime-edit-second-field]:p-0`,
              `[&::-webkit-datetime-edit-millisecond-field]:p-0`,
              `[&::-webkit-datetime-edit-meridiem-field]:p-0`,
            ],
          CONTROL_HEIGHT_CLASS,
          `relative block w-full appearance-none rounded-lg px-[calc(--spacing(3.5)-1px)] sm:px-[calc(--spacing(3)-1px)]`,
          `text-base/6 text-foreground placeholder:text-muted sm:text-sm/6`,
          `border border-control-border data-hover:border-primary/40`,
          `bg-transparent`,
          `focus:outline-hidden`,
          `data-invalid:border-danger data-invalid:data-hover:border-danger`,
          `data-disabled:border-control-border data-disabled:bg-surface data-hover:data-disabled:border-control-border`,
        ])}
      />
    </span>
  )
})
